import React, { useRef, useState, useEffect, useMemo } from "react";
import { useEditor, TLShapeId, useValue, TLUnknownShape, TLShape } from "@tldraw/tldraw";
import { DndContext, DragOverlay, useDroppable } from "@dnd-kit/core";
import { generateKeyBetween } from "fractional-indexing";
import ShapeItem, { getShapeIcon } from "./ShapeItemComponent";
import { getShapeName } from "pages/flyer/builder/utils";
import { EyeOpenIcon16, EyeClosedIcon16, LockUnlockedIcon, LockLockedIcon } from "./icons";
import "./style.css";

interface ShapeListProps {
  shapeIds: TLShapeId[];
  depth: number;
  parentIsSelected?: boolean;
  parentIsHidden?: boolean;
  shapes?: any[];
}

export function ShapeList({
  shapeIds,
  depth,
  parentIsSelected,
  parentIsHidden,
  shapes: initialShapes,
}: ShapeListProps) {
  const [shapes, setShapes] = useState<any>(initialShapes);
  const [selectedShape, setSelectedShape] = useState<TLUnknownShape | null>(null);
  const [activeDragId, setActiveDragId] = useState<TLShapeId | null>(null);

  const editor = useEditor();
  const selectedShapeIds = useValue("isSelected", () => editor.getSelectedShapeIds(), [editor]);

  const { isOver, setNodeRef } = useDroppable({
    id: "droppable",
  });

  const style = {
    color: isOver ? "green" : undefined,
  };

  const firstSelectedIndexRef = useRef<number | null>(null);

  // Hide mask groups unless they are in edit mode
  const visibleShapes = useMemo(
    () =>
      (shapes || []).filter((shape: any) => {
        // Hide preview shapes always
        if (shape?.meta?.isPreviewShape) return false;
        if (shape?.type === "group" && shape?.meta?.isMaskGroup && shape?.meta?.inEditMode !== true) {
          return false;
        }
        return true;
      }),
    [shapes]
  );

  const handleItemClick = (shapeId: TLShapeId, index: number, shiftKey?: boolean) => {
    if (shiftKey) {
      const selectedShapeIds = editor.getSelectedShapeIds();
      if (firstSelectedIndexRef.current === null || selectedShapeIds.length === 0) {
        // Start a new range selection
        firstSelectedIndexRef.current = shapeIds.findIndex((id) => id === shapeId);
      }
      const currentIndex = shapeIds.findIndex((id) => id === shapeId);
      if (firstSelectedIndexRef.current === -1 || currentIndex === -1) {
        console.warn("Failed to find indices for range selection.");
        return;
      }
      const startIndex = Math.min(firstSelectedIndexRef.current, currentIndex);
      const endIndex = Math.max(firstSelectedIndexRef.current, currentIndex);
      const rangeIds = shapeIds.slice(startIndex, endIndex + 1);
      // Merge with existing selection, deduplicate via Set
      const existingIds = editor.getSelectedShapeIds();
      const mergedIds = Array.from(new Set([...existingIds, ...rangeIds]));
      const shapesToSelect = mergedIds.map((id) => editor.getShape(id)).filter(Boolean) as TLShape[];
      if (shapesToSelect.length > 0) {
        editor.select(...shapesToSelect);
      }
    } else {
      // Save this index as anchor for future shift+click range selection
      firstSelectedIndexRef.current = shapeIds.findIndex((id) => id === shapeId);
      const shape = editor.getShape(shapeId);
      if (shape) {
        editor.select(shape);
      }
    }
  };

  // Synchronize selected shape on selection change
  useEffect(() => {
    if (selectedShapeIds.length === 1) {
      const shape = editor.getShape(selectedShapeIds[0]);
      setSelectedShape(shape || null);
    } else {
      setSelectedShape(null); // Clear if more than one shape is selected
    }
  }, [selectedShapeIds, editor]);

  // Synchronize shapes with shapeIds
  useEffect(() => {
    const updateShapes = () => {
      const updatedShapes = shapeIds.map((id) => editor.getShape(id)).filter(Boolean);
      // Sort by index to reflect the current z-order, since shapeIds may still
      // have the old order when this fires before the parent re-renders.
      updatedShapes.sort((a: any, b: any) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0));
      setShapes(updatedShapes);
    };

    updateShapes();

    // Coalesce shape-change events into one update per frame. The handler
    // fires once per *shape mutation*, so dragging N selected shapes
    // produced N invocations of `updateShapes` per pointer move — each
    // rebuilding + sorting the full `shapeIds` array. At 128 selected
    // that's O(N²) per move and the layers panel becomes the bottleneck.
    let pendingRaf: number | null = null;
    const scheduleUpdate = () => {
      if (pendingRaf !== null) return;
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        updateShapes();
      });
    };
    const unsubscribe = editor.sideEffects.registerAfterChangeHandler("shape", scheduleUpdate);

    return () => {
      unsubscribe(); // Clean up on unmount
      if (pendingRaf !== null) cancelAnimationFrame(pendingRaf);
    };
  }, [shapeIds, editor]);

  // Deduplicate shapes by their `id` and ensure new shapes are appended to the end
  useEffect(() => {
    setShapes((prevShapes: any) => {
      const uniqueShapes = new Map();
      (prevShapes || []).forEach((shape: any) => {
        uniqueShapes.set(shape.id, shape);
      });
      (initialShapes || []).forEach((shape: any) => {
        if (!uniqueShapes.has(shape.id)) {
          uniqueShapes.set(shape.id, shape); // Append new shapes
        }
      });
      return Array.from(uniqueShapes.values()).sort((a: any, b: any) =>
        a.index < b.index ? -1 : a.index > b.index ? 1 : 0
      );
    });
  }, [initialShapes]);

  // Register sideEffects to track shape changes and additions
  useEffect(() => {
    const handleShapeChange = (changedShape: any) => {
      if (selectedShape && changedShape.id === selectedShape.id) {
        setSelectedShape(changedShape); // Update the selected shape
      }
    };

    const handleShapeAddition = (newShape: any) => {
      // Only add shapes that belong to the current list (i.e., no parent or matching parent)
      if (!newShape.parentId || shapeIds.includes(newShape.parentId)) {
        setShapes((prevShapes: any) => {
          const updatedShapes = [...(prevShapes || []), newShape];
          return updatedShapes.sort((a: any, b: any) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0)); // Ensure consistent order
        });
      }
    };

    const unregisterChangeHandler = editor.sideEffects.registerAfterChangeHandler("shape", handleShapeChange);
    const unregisterAdditionHandler = editor.sideEffects.registerAfterCreateHandler("shape", handleShapeAddition);

    return () => {
      unregisterChangeHandler(); // Clean up on unmount
      unregisterAdditionHandler(); // Clean up on unmount
    };
  }, [selectedShape, shapeIds, editor]);

  const handleDragOver = (event: any) => {
    const { active, over } = event;

    if (!active.id || !over?.id || active.id === over.id) return;

    const overShapeIndex = shapes?.findIndex((shape: any) => shape.id === over.id);
    const draggedShapeIndex = shapes?.findIndex((shape: any) => shape.id === active.id);

    if (overShapeIndex === -1 || draggedShapeIndex === -1) return;

    // Determine drag position relative to the target
    const dragOverPosition = draggedShapeIndex < overShapeIndex ? "above" : "below";
    // @ts-ignore
    editor.inputs.dragOverPosition = dragOverPosition; // Store position globally
  };

  const handleDragStart = (event: any) => {
    setActiveDragId(event.active.id as TLShapeId);
  };

  const handleDragEnd = (event: any) => {
    setActiveDragId(null);
    const { active, over } = event;

    if (!active.id || !over?.id || active.id === over.id) return;

    const draggedShape = shapes?.find((shape: any) => shape.id === active.id);
    const overShapeIndex = shapes?.findIndex((shape: any) => shape.id === over.id);
    const draggedShapeIndex = shapes?.findIndex((shape: any) => shape.id === active.id);

    if (!draggedShape || overShapeIndex === -1 || draggedShapeIndex === -1) return;

    let before, after;

    if (draggedShapeIndex! < overShapeIndex!) {
      // Мoved down
      before = shapes[overShapeIndex];
      after = shapes[overShapeIndex + 1] ?? null;
    } else {
      // Moved up
      before = shapes[overShapeIndex - 1] ?? null;
      after = shapes[overShapeIndex];
    }

    let newIndex: string;

    try {
      if (before?.index === after?.index) {
        // Avoid duplicate indices
        newIndex = generateKeyBetween(after?.index, null);
      } else {
        newIndex = generateKeyBetween(before?.index ?? null, after?.index ?? null);
      }
    } catch (error) {
      newIndex = generateKeyBetween(shapes[shapes.length - 1]?.index ?? null, null);
    }

    const updatedShapes = shapes.map((shape: any) =>
      shape.id === draggedShape.id ? { ...shape, index: newIndex } : shape
    );

    const sortedShapes = updatedShapes.sort((a: { index: string }, b: { index: string }) =>
      a.index < b.index ? -1 : a.index > b.index ? 1 : 0
    );
    setShapes(sortedShapes);

    sortedShapes.forEach((shape: any) => {
      editor.updateShape({
        id: shape.id,
        index: shape.index,
        type: shape.type,
        isLocked: false,
      });
    });
  };

  return (
    <div className="shape-tree h-[fit-content] overflow-y-auto overflow-x-hidden">
      <DndContext onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div
          ref={setNodeRef}
          className="droppable overflow-hidden"
          style={{ ...style, display: "flex", flexDirection: "column", gap: 8 }}
        >
          {visibleShapes
            ?.slice()
            .reverse()
            .map((shape: any, index: number) => (
              <ShapeItem
                key={shape.id}
                shapeId={shape.id}
                depth={depth}
                parentIsHidden={parentIsHidden}
                parentIsSelected={parentIsSelected}
                index={index}
                shape={shape}
                onClick={(shiftKey) => handleItemClick(shape.id, index, shiftKey)}
              />
            ))}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDragId
            ? (() => {
                const shape = shapes?.find((s: any) => s.id === activeDragId);
                const idx = visibleShapes?.findIndex((s: any) => s.id === activeDragId) ?? 0;
                const name = getShapeName(editor, activeDragId, idx) || shape?.meta?.name || "Shape";
                return (
                  <div
                    style={{
                      background: "#E2E2FC",
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                      width: 210,
                      height: 28,
                      paddingLeft: 21,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", flexShrink: 0, width: 16, height: 16 }}>
                        {getShapeIcon(shape)}
                      </span>
                      <span
                        className="capitalize-first"
                        style={{
                          color: "#0C172C",
                          fontFeatureSettings: "'liga' off, 'clig' off",
                          fontFamily: "Inter",
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "16px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 70,
                        }}
                      >
                        {name}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                      <span className="shape-visibility-toggle">
                        {shape?.isLocked ? <LockLockedIcon /> : <LockUnlockedIcon />}
                      </span>
                      <span className="shape-visibility-toggle">
                        {shape?.meta?.hidden ? <EyeClosedIcon16 /> : <EyeOpenIcon16 />}
                      </span>
                    </div>
                  </div>
                );
              })()
            : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
